import React, { useState, useCallback } from 'react';
import IndicatorDashboard from '../components/IndicatorDashboard';
import { fetchHistorical } from '../api';
import { useAppData } from '../AppDataContext';

const TIMEFRAMES = ['5m', '3m'];

function extractBuyStatus(data) {
  if (!data) return { signal: 'WAITING', label: 'WAITING' };
  const signal = data.intraday_buy_signal
    || data.indicators?.find(i => i.name?.startsWith('Intraday Buy Setup'))?.signal
    || 'WAITING';
  return {
    signal,
    label: signal === 'BULLISH' ? 'BUY READY' : signal === 'WAITING' ? 'WAITING' : 'NO BUY',
  };
}

export default function Indicators() {
  const { indicatorsByTf } = useAppData();
  const liveByTf = indicatorsByTf;
  const loading = Object.keys(liveByTf).length === 0;
  const error = null;

  // Historical
  const [histDate, setHistDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [histDataByTf, setHistDataByTf] = useState({});
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);
  const [selectedSnapByTf, setSelectedSnapByTf] = useState({});
  const [mode, setMode] = useState('live');

  const loadHistorical = useCallback(async () => {
    setHistLoading(true);
    setHistError(null);
    setHistDataByTf({});
    setSelectedSnapByTf({});
    try {
      const results = await Promise.all(TIMEFRAMES.map(tf => fetchHistorical(histDate, tf)));
      const nextData = {};
      const nextSelected = {};
      let hasError = false;

      results.forEach((res, idx) => {
        const tf = TIMEFRAMES[idx];
        if (res.data?.status === 'ok') {
          nextData[tf] = res.data;
          if (res.data.snapshots?.length > 0) {
            nextSelected[tf] = res.data.snapshots[res.data.snapshots.length - 1];
          }
        } else {
          hasError = true;
        }
      });

      setHistDataByTf(nextData);
      setSelectedSnapByTf(nextSelected);
      if (hasError && Object.keys(nextData).length === 0) {
        setHistError('Failed to load historical data');
      }
    } catch (err) {
      setHistError('Failed to load historical data');
    }
    setHistLoading(false);
  }, [histDate]);

  const liveSignal5 = extractBuyStatus(liveByTf['5m']);
  const liveSignal3 = extractBuyStatus(liveByTf['3m']);

  return (
    <div className="page-indicators">
      <div className="page-header-row">
        <h1 className="page-title">Indicators</h1>
        <div className="mode-toggle">
          <button className={`tab-btn ${mode === 'live' ? 'active' : ''}`} onClick={() => setMode('live')}>Live</button>
          <button className={`tab-btn ${mode === 'historical' ? 'active' : ''}`} onClick={() => setMode('historical')}>Historical</button>
        </div>
      </div>

      <section className="intraday-signal-strip">
        <div className={`intraday-signal-card ${liveSignal5.signal === 'BULLISH' ? 'on' : 'off'}`}>
          <span className="intraday-signal-tag">5 Min</span>
          <span className="intraday-signal-text">{liveSignal5.label}</span>
        </div>
        <div className={`intraday-signal-card ${liveSignal3.signal === 'BULLISH' ? 'on' : 'off'}`}>
          <span className="intraday-signal-tag">3 Min</span>
          <span className="intraday-signal-text">{liveSignal3.label}</span>
        </div>
      </section>

      {mode === 'live' && (
        <>
          {loading && Object.keys(liveByTf).length === 0 && (
            <div className="loading">
              <div className="spinner" />
              <p>Connecting to backend...</p>
            </div>
          )}
          {error && Object.keys(liveByTf).length === 0 && <div className="error-banner">{error}</div>}

          {TIMEFRAMES.map(tf => (
            liveByTf[tf] && (
              <section key={tf} className="timeframe-block">
                <div className="timeframe-block-header">
                  <h2>{tf === '5m' ? '5 Min Indicators' : '3 Min Indicators'}</h2>
                </div>
                <IndicatorDashboard data={liveByTf[tf]} />
              </section>
            )
          ))}
        </>
      )}

      {mode === 'historical' && (
        <div className="historical-view">
          <div className="hist-controls">
            <input type="date" value={histDate} onChange={e => setHistDate(e.target.value)} />
            <button className="btn-primary" onClick={loadHistorical} disabled={histLoading}>
              {histLoading ? 'Loading...' : 'Load'}
            </button>
          </div>
          {histError && <div className="error-banner">{histError}</div>}

          {TIMEFRAMES.map(tf => {
            const histData = histDataByTf[tf];
            const selectedSnap = selectedSnapByTf[tf];
            if (!histData || !histData.snapshots) return null;
            return (
              <section key={`hist-${tf}`} className="timeframe-block">
                <div className="timeframe-block-header">
                  <h2>{tf === '5m' ? 'Historical 5 Min' : 'Historical 3 Min'}</h2>
                </div>
                <div className="hist-timeline">
                  <div className="timeline-bar">
                    {histData.snapshots.map((snap, i) => (
                      <button
                        key={i}
                        className={`timeline-dot ${selectedSnap === snap ? 'selected' : ''}`}
                        onClick={() => setSelectedSnapByTf(prev => ({ ...prev, [tf]: snap }))}
                        title={snap.timestamp}
                      >
                        {snap.timestamp?.split(' ')[1]?.substring(0, 5)}
                      </button>
                    ))}
                  </div>
                </div>
                {selectedSnap && (
                  <IndicatorDashboard data={selectedSnap} timeLabel={selectedSnap.timestamp} />
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
